/*
  Copyright 2018-2020 National Geographic Society

  Use of this software does not constitute endorsement by National Geographic
  Society (NGS). The NGS name and NGS logo may not be used for any purpose without
  written permission from NGS.

  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import Box from '@material-ui/core/Box';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Typography from '@material-ui/core/Typography';
import React from 'react';

interface ILegendItemGroupComponent {
  activeLayer?: any;
  layerGroup?: any;
  index?: number;
  templates?: any;
  onChangeInfo?: (activeLayer: any, value: any) => void;
  onChangeOpacity?: (activeLayer: any, value: any) => void;
  onChangeVisibility?: (activeLayer: any, value: any) => void;
  onChangeOrder?: (activeLayer: any, value: any) => void;
  onChangeCurrent?: (activeLayer: any, value: any) => void;
  onChangeLayerDate?: (activeLayer: any, value: any) => void;
}

class LegendItemGroupComponent extends React.PureComponent<ILegendItemGroupComponent> {
  public static propTypes = {};

  // Remove the defaultProps that's causing the issue
  public static defaultProps = {};

  public onChangeCurrent = (e) => {
    const { activeLayer, onChangeCurrent } = this.props;
    onChangeCurrent(activeLayer, e.currentTarget.value);
  };

  public render() {
    const { activeLayer: propActiveLayer, layerGroup } = this.props;

    // Get activeLayer from props or derive it from layerGroup
    // Check if propActiveLayer is a meaningful object (not just empty {})
    const isValidActiveLayer = propActiveLayer && Object.keys(propActiveLayer).length > 0;
    const activeLayer = isValidActiveLayer
      ? propActiveLayer
      : layerGroup?.layers?.find((l) => l.active) || layerGroup?.layers?.[0];

    if (!activeLayer || Object.keys(activeLayer).length === 0) {
      console.warn('LegendItemGroup: No active layer found', { propActiveLayer, layerGroup });
      return null;
    }

    // console.log('LegendItemGroup: Using activeLayer:', activeLayer);

    const { current, source, references } = activeLayer;

    // Handle different source types and check for legendConfig
    if (!source) {
      console.warn('LegendItemGroup: activeLayer.source is undefined', activeLayer);
      return null;
    }

    const { type } = source;

    // Accept both 'group' and 'raster' types
    if (type === 'raster') {
      // For raster layers, render legendConfig if available
      if (activeLayer.legendConfig && activeLayer.legendConfig.items) {
        // console.log(
        //   'LegendItemGroup: Rendering raster legend with',
        //   activeLayer.legendConfig.items.length,
        //   'items'
        // );
        return (
          <Box mb={1}>
            {activeLayer.legendConfig.items.map((item, idx) => (
              <Box key={idx} display="flex" alignItems="center" mb={0.5}>
                <Box
                  width={16}
                  height={16}
                  mr={1}
                  style={{
                    backgroundColor: item.color,
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.2)',
                  }}
                />
                <Typography variant="caption" style={{ fontSize: '11px' }}>
                  {item.name}
                </Typography>
              </Box>
            ))}
          </Box>
        );
      } else {
        console.warn('LegendItemGroup: Raster layer missing legendConfig');
        return null;
      }
    } else if (type !== 'group') {
      console.warn('LegendItemGroup: Unsupported source type:', type);
      return null;
    }

    // Original group logic for layers with references
    if (!references || references.length === 0) {
      return null;
    }

    return (
      <Box mb={1}>
        <RadioGroup className="marapp-qa-layergroup">
          {references.map((l) => {
            const currentActive = current || references[0].id;
            const checked = currentActive === l.id;

            return (
              <FormControlLabel
                key={l.id}
                value={l.id}
                id={`layer-group-${l.id}`}
                control={<Radio size="small" />}
                onChange={this.onChangeCurrent}
                label={<Typography variant="body2">{l.name}</Typography>}
                checked={checked}
              />
            );
          })}
        </RadioGroup>
      </Box>
    );
  }
}

export default LegendItemGroupComponent;
